import React, { useEffect, useState } from 'react';
import Button from '../../ui/Button';
import Card from '../../ui/Card';
import StatusBadge from '../../ui/Status/StatusBadge';
import { Select, Input } from '../../ui/Field/Field';
import { finesService, errorMessage } from '../../services/finesService';
import { dubaiIsoDay } from '../../utils/dubaiTime';

/**
 * The Salik account on the Salik and Parking tabs: how the engine signs in to Salik and whether its pulls come in.
 * The engine signs in with the Salik username + password an admin keeps here FIRST, and only when that does not go
 * through with the RTA account and RTA's code read from the mailbox kept here (owner, 4 Oct 2026: "نخلى الotp مرحلة
 * تانيه لو مشتغلش بالمرحلة الاولى"); the code box shows only when that mail did not come (the phone / Telegram). The pasted session and
 * the pull through the user's own browser went (owner, 2 Oct 2026: "ايه كل الازرار دى ملهاش لازمة خالص واصلا التوكين
 * مش بيشتغل فملموش لازمة الكوكيز بتاعه" - a session made on another PC never opens the portal from the server).
 */
const isoDay = (d) => dubaiIsoDay(d);   // the Dubai day, whatever the browser's zone

/** The engine's wall time ("YYYY-MM-DD HH:MM:SS", Dubai) the app's way: "dd/mm/yyyy HH:MM". */
const wall = (t) => {
    const s = String(t || '');
    return s.length >= 16 && s[4] === '-' ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)} ${s.slice(11, 16)}` : (s || '?');
};

// how the last sign-in got in (owner, 4 Oct 2026: "ازاى اعرف مين اللى شغال بالotp ولا العادى")
const SIGNIN_WAY = { username: 'the Salik username (no code)', rta: 'the RTA account + its code', session: 'the kept session' };

const BLANK_MAIL = { kind: 'gmail', tenant: '', client: '', secret: '', mailbox: '' };

// ``isAdmin``: only an admin changes the RTA account and the mailbox (owner, 2 Oct 2026); ``canWrite`` signs in / tests
export default function SalikSessionCard({ info, onChange, canWrite, isAdmin = false }) {
    const [msg, setMsg] = useState(null);
    const running = !!info?.running;
    // while a pull runs, read the status again until it ends: its verdict shows here without a reload
    useEffect(() => {
        if (!running) return undefined;
        const t = setTimeout(() => { onChange(); }, 5000);
        return () => clearTimeout(t);
    }, [running, info, onChange]);

    // the first way in (4 Oct 2026): the Salik username + password an admin types here (else the server's own account)
    const [userEdit, setUserEdit] = useState(false);
    const [salikUser, setSalikUser] = useState('');
    const [salikPass, setSalikPass] = useState('');
    const [userInfo, setUserInfo] = useState(null);
    const userKept = !!info?.user?.configured;
    useEffect(() => {
        if (!canWrite) { setUserInfo(null); return undefined; }
        let live = true;
        finesService.salikUser().then((r) => { if (live) setUserInfo(r); }).catch(() => { if (live) setUserInfo(null); });
        return () => { live = false; };
    }, [canWrite, userKept]);
    // the RTA way in (owner, 2 Oct 2026): the account an admin types here, and the code RTA sends
    const [otpCode, setOtpCode] = useState('');
    const [rtaEdit, setRtaEdit] = useState(false);
    const [rtaUser, setRtaUser] = useState('');
    const [rtaPass, setRtaPass] = useState('');
    const [rtaBusy, setRtaBusy] = useState(false);
    // the username of the kept RTA account: the admins' own read (the status every viewer reads says only "kept")
    const [rtaInfo, setRtaInfo] = useState(null);
    const rtaKept = !!info?.rta?.configured;
    useEffect(() => {
        if (!canWrite || !rtaKept) { setRtaInfo(null); return undefined; }
        let live = true;
        finesService.salikRta().then((r) => { if (live) setRtaInfo(r); }).catch(() => { if (live) setRtaInfo(null); });
        return () => { live = false; };
    }, [canWrite, rtaKept]);
    // the mailbox RTA sends the code to (owner, 2 Oct 2026): the engine reads the code itself - a Gmail box (its App
    // Password), or the Microsoft 365 app, which needs the company admin's consent
    const [mailEdit, setMailEdit] = useState(false);
    const [mailForm, setMailForm] = useState(BLANK_MAIL);
    const [mailInfo, setMailInfo] = useState(null);
    const mailKept = !!info?.mail?.configured;
    useEffect(() => {
        if (!canWrite || !mailKept) { setMailInfo(null); return undefined; }
        let live = true;
        finesService.salikMail().then((r) => { if (live) setMailInfo(r); }).catch(() => { if (live) setMailInfo(null); });
        return () => { live = false; };
    }, [canWrite, mailKept]);
    if (!info || info.error || !info.enabled) return null;

    const failing = !!info.last_error;
    const rta = info.rta || {};
    const act = async (fn, okText) => {
        setRtaBusy(true); setMsg(null);
        try { await fn(); if (okText) setMsg({ ok: true, text: okText }); await onChange(); return true; }
        catch (e) { setMsg({ ok: false, text: errorMessage(e) }); return false; }
        finally { setRtaBusy(false); }
    };
    const sendCode = () => act(() => finesService.salikOtp(otpCode.trim()), 'Code sent - the engine is signing in with it…')
        .then((ok) => { if (ok) setOtpCode(''); });
    const saveUser = () => act(() => finesService.salikUserSave(salikUser.trim(), salikPass), 'Salik account saved - the next sign-in tries it first.')
        .then((ok) => { if (ok) { setSalikPass(''); setUserEdit(false); finesService.salikUser().then(setUserInfo).catch(() => {}); } });
    const saveRta = () => act(() => finesService.salikRtaSave(rtaUser.trim(), rtaPass), 'RTA account saved - the engine signs in with it.')
        .then((ok) => { if (ok) { setRtaPass(''); setRtaEdit(false); } });
    // Microsoft's / Gmail's times are UTC ("...Z"): shown in Dubai time
    const dubai = (iso) => {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit',
            year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '');
    };
    const mailWords = (t) => `Mailbox opens - read ${t.read} of its newest messages`
        + (t.rta?.length ? `; RTA mails: ${t.rta.map((m) => `${dubai(m.received)} "${m.subject}"`).join(', ')}` : '; no RTA mail among them yet');
    const saveMail = () => act(async () => {
        const r = await finesService.salikMailSave(mailForm.kind === 'gmail'
            ? { kind: 'gmail', mailbox: mailForm.mailbox.trim(), secret: mailForm.secret }
            : { kind: 'graph', tenant: mailForm.tenant.trim(), client: mailForm.client.trim(), secret: mailForm.secret, mailbox: mailForm.mailbox.trim() });
        setMailInfo(r);
        setMsg({ ok: true, text: `${mailWords(r.test)} - saved; RTA's code now goes to this mailbox and the engine reads it.` });
    }).then((ok) => { if (ok) { setMailForm(BLANK_MAIL); setMailEdit(false); } });
    const testMail = () => act(async () => { const t = await finesService.salikMailTest(); setMsg({ ok: true, text: `${mailWords(t)}.` }); });
    const mailFilled = !!(mailForm.secret && mailForm.mailbox.trim()
        && (mailForm.kind === 'gmail' || (mailForm.tenant.trim() && mailForm.client.trim())));
    const signInNow = () => {
        const today = isoDay(new Date());
        return act(() => finesService.salikSync({ kind: 'toll', startDate: today, endDate: today }),
            'Signing in - with the username first, else the RTA account and its code.');
    };
    const session = info.session || {};
    // the last REAL pull: salik.db's own stamps first - the scheduler's memory starts empty after every engine restart
    const stamps = [info.counts?.toll?.lastSync, info.counts?.parking?.lastSync, info.lastIntraday, info.last_run].filter(Boolean).map(String).sort();
    const lastPull = stamps[stamps.length - 1];
    // pulls going on (the RTA account, a kept session): the username's pause is a side note, not "paused" (2 Oct 2026:
    // the RTA sign-in worked and pulled 89 trips while the card still said "sign-in paused" in red)
    const pulledAt = lastPull ? new Date(`${String(lastPull).replace(' ', 'T')}+04:00`).getTime() : NaN;
    const freshPull = !failing && Number.isFinite(pulledAt) && Date.now() - pulledAt < (2 * (Number(info.intervalMin) || 30) + 10) * 60000;
    // a failed sign-in: the engine does not sign in again until then. Two ways in (4 Oct 2026): the username first, the
    // RTA account second - "waits" only while NEITHER may sign in, until the sooner of the two
    const rtaWait = rta.configured && rta.pause && !String(rta.pause.reason || '').startsWith('signed in with the RTA account') ? rta.pause : null;
    // an engine from before 4 Oct 2026 sends no `user`: its username is the server's, its pause the session's
    const acct = info.user || { serverAccount: true, pause: session.signinPausedUntil
        ? { until: session.signinPausedUntil, reason: session.signinPauseReason || 'the last sign-in failed' } : null };
    const userWait = (acct.configured || acct.serverAccount) && acct.pause ? acct.pause : null;
    const userOpen = (acct.configured || acct.serverAccount) && !userWait;
    const rtaOpen = rta.configured && !rtaWait;
    const waits = [userWait?.until, rtaWait?.until].filter(Boolean).sort();
    const paused = !freshPull && !userOpen && !rtaOpen ? waits[0] || null : null;

    return (
        <Card padding="none" className={failing ? 'border-red-300' : undefined}><div className="px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0 sm:min-w-[260px]">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
                    Salik account <StatusBadge tone={running ? 'info' : failing || paused ? 'danger' : 'success'}
                        label={running ? 'pulling now' : paused ? `sign-in waits until ${wall(paused).slice(11)}` : failing ? 'not pulling' : 'working'} />
                </div>
                <div className="text-sm text-gray-600">
                    {failing
                        ? <span className="text-red-700" title={String(info.last_error)}>{info.errorWords || `${String(info.last_error).slice(0, 140)}…`}</span>
                        : <>last pull {wall(lastPull)}</>}
                    {' · '}{session.lastSignin?.at
                        ? <>signed in {wall(session.lastSignin.at)} with <b>{SIGNIN_WAY[session.lastSignin.way] || session.lastSignin.way}</b></>
                        : 'signed in by the engine'}
                    {session.renewedAt ? <> · renewed by Salik {wall(session.renewedAt)}</> : null}
                </div>
                {freshPull && userWait && rta.configured && (
                    <div className="text-xs text-gray-500">the username sign-in waits until {wall(userWait.until).slice(11)} - the RTA account signs in meanwhile</div>
                )}
                {!freshPull && !running && !failing && paused && (
                    <div className="text-sm text-red-700">{[userWait && `username: waits until ${wall(userWait.until).slice(11)} - ${userWait.reason}`,
                        rtaWait && `RTA account: waits until ${wall(rtaWait.until).slice(11)} - ${rtaWait.reason}`].filter(Boolean).join(' · ')}</div>
                )}
                {msg && <div role={msg.ok ? 'status' : 'alert'} className={`text-sm font-medium mt-1 ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</div>}
            </div>
            {canWrite && info.otp && (
                <div className="w-full flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" role="alert">
                    <span className="text-sm font-semibold text-gray-900">Salik (RTA sign-in) needs the code RTA just sent{info.otp.to === 'e-mail' ? ' to the company e-mail' : ' to your phone'}</span>
                    <span className="text-xs text-gray-600">until {wall(info.otp.until).slice(11)} - or reply with it on Telegram</span>
                    <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 8))} inputMode="numeric"
                        autoComplete="one-time-code" placeholder="code" aria-label="RTA code" className="w-32 font-mono" autoFocus />
                    <Button size="sm" variant="primary" disabled={rtaBusy || otpCode.length < 4} isLoading={rtaBusy} onClick={sendCode}>Send code</Button>
                </div>
            )}
            {canWrite && (
                <div className="w-full flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-semibold text-gray-900">Salik username</span>
                    {(userKept || acct.serverAccount || !info.user) && !userEdit ? (
                        <>
                            <span className="text-gray-700 font-mono">{userKept ? (userInfo?.user || 'kept') : "the server's account"}</span>
                            {userWait ? <span className="text-red-700">waits until {wall(userWait.until).slice(11)} - {userWait.reason}</span>
                                : <span className="text-gray-500">signs in first - no code needed</span>}
                            {isAdmin && <Button size="sm" variant="ghost" disabled={rtaBusy} onClick={() => { setSalikUser(userInfo?.user || ''); setUserEdit(true); }}>{userKept ? 'Change' : 'Set the new account'}</Button>}
                        </>
                    ) : !isAdmin ? (
                        <span className="text-gray-500">not set - an admin sets it</span>
                    ) : (
                        <>
                            <Input value={salikUser} onChange={(e) => setSalikUser(e.target.value)} placeholder="Salik username" aria-label="Salik username"
                                autoComplete="off" className="w-48" />
                            <Input type="password" value={salikPass} onChange={(e) => setSalikPass(e.target.value)} placeholder="Salik password"
                                aria-label="Salik password" autoComplete="new-password" className="w-48" />
                            <Button size="sm" variant="primary" disabled={rtaBusy || !salikUser.trim() || !salikPass} isLoading={rtaBusy} onClick={saveUser}
                                aria-label="Save the Salik username">Save</Button>
                            {userEdit && <Button size="sm" disabled={rtaBusy} onClick={() => { setUserEdit(false); setSalikPass(''); }}>Cancel</Button>}
                            <span className="text-xs text-gray-500">kept encrypted on the server, never shown again</span>
                        </>
                    )}
                </div>
            )}
            {canWrite && (
                <div className="w-full flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-semibold text-gray-900">RTA account</span>
                    {rta.configured && !rtaEdit ? (
                        <>
                            <span className="text-gray-700 font-mono">{rtaInfo?.user || 'kept'}</span>
                            {rta.pause && String(rta.pause.reason || '').startsWith('signed in with the RTA account')
                                ? <span className="text-green-700">signed in - the next RTA sign-in not before {wall(rta.pause.until).slice(11)}</span>
                                : rta.pause ? <span className="text-red-700">waits until {wall(rta.pause.until).slice(11)} - {rta.pause.reason}</span>
                                : <span className="text-gray-500">{mailKept ? 'used when the username does not sign in - its code is read from the e-mail' : 'used when the username does not sign in (a code comes to your phone)'}</span>}
                            <Button size="sm" variant="secondary" disabled={rtaBusy || running} onClick={signInNow}
                                title="Signs in to Salik now - with the username first, else the RTA account and its code">Sign in now</Button>
                            {isAdmin && <Button size="sm" variant="ghost" disabled={rtaBusy} onClick={() => { setRtaUser(rtaInfo?.user || ''); setRtaEdit(true); }}>Change</Button>}
                        </>
                    ) : !isAdmin ? (
                        <span className="text-gray-500">not set - an admin sets it</span>
                    ) : (
                        <>
                            <Input value={rtaUser} onChange={(e) => setRtaUser(e.target.value)} placeholder="RTA username" aria-label="RTA username"
                                autoComplete="off" className="w-48" />
                            <Input type="password" value={rtaPass} onChange={(e) => setRtaPass(e.target.value)} placeholder="RTA password"
                                aria-label="RTA password" autoComplete="new-password" className="w-48" />
                            <Button size="sm" variant="primary" disabled={rtaBusy || !rtaUser.trim() || !rtaPass} isLoading={rtaBusy} onClick={saveRta}>Save</Button>
                            {rtaEdit && <Button size="sm" disabled={rtaBusy} onClick={() => { setRtaEdit(false); setRtaPass(''); }}>Cancel</Button>}
                            <span className="text-xs text-gray-500">kept encrypted on the server, never shown again</span>
                        </>
                    )}
                </div>
            )}
            {canWrite && rta.configured && (
                <div className="w-full flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-semibold text-gray-900">RTA code by e-mail</span>
                    {mailKept && !mailEdit ? (
                        <>
                            <span className="text-gray-700 font-mono">{mailInfo?.mailbox || 'kept'}</span>
                            {mailInfo?.kind && <span className="text-xs text-gray-500">{mailInfo.kind === 'gmail' ? 'Gmail' : 'Microsoft 365'}</span>}
                            <span className="text-gray-500">RTA sends the code here and the engine reads it</span>
                            <Button size="sm" variant="secondary" disabled={rtaBusy} onClick={testMail}
                                title="Reads the mailbox now: how many of the newest messages, and RTA's own mails (time and subject only)">Test</Button>
                            {isAdmin && <Button size="sm" variant="ghost" disabled={rtaBusy} onClick={() => { setMailForm({ ...BLANK_MAIL, kind: mailInfo?.kind || 'gmail', mailbox: mailInfo?.mailbox || '' }); setMailEdit(true); }}>Change</Button>}
                        </>
                    ) : !isAdmin ? (
                        <span className="text-gray-500">not set - an admin sets it</span>
                    ) : (
                        <>
                            <Select value={mailForm.kind} onChange={(e) => setMailForm((f) => ({ ...f, kind: e.target.value, secret: '' }))}
                                aria-label="Mail service" className="w-auto">
                                <option value="gmail">Gmail</option>
                                <option value="graph">Microsoft 365</option>
                            </Select>
                            {mailForm.kind === 'graph' && (
                                <>
                                    <Input value={mailForm.tenant} onChange={(e) => setMailForm((f) => ({ ...f, tenant: e.target.value }))} placeholder="Directory (tenant) ID"
                                        aria-label="Directory (tenant) ID" autoComplete="off" className="w-72 font-mono" />
                                    <Input value={mailForm.client} onChange={(e) => setMailForm((f) => ({ ...f, client: e.target.value }))} placeholder="Application (client) ID"
                                        aria-label="Application (client) ID" autoComplete="off" className="w-72 font-mono" />
                                </>
                            )}
                            <Input value={mailForm.mailbox} onChange={(e) => setMailForm((f) => ({ ...f, mailbox: e.target.value }))}
                                placeholder={mailForm.kind === 'gmail' ? 'Gmail address RTA sends the code to' : 'Mailbox RTA sends the code to'}
                                aria-label="Mailbox" autoComplete="off" className="w-64" />
                            <Input type="password" value={mailForm.secret} onChange={(e) => setMailForm((f) => ({ ...f, secret: e.target.value }))}
                                placeholder={mailForm.kind === 'gmail' ? 'App Password (16 letters)' : 'Client secret (Value)'}
                                aria-label={mailForm.kind === 'gmail' ? 'App Password' : 'Client secret'} autoComplete="new-password" className="w-56" />
                            <Button size="sm" variant="primary" disabled={rtaBusy || !mailFilled} isLoading={rtaBusy} onClick={saveMail}>Save</Button>
                            {mailEdit && <Button size="sm" disabled={rtaBusy} onClick={() => { setMailEdit(false); setMailForm(BLANK_MAIL); }}>Cancel</Button>}
                            <span className="text-xs text-gray-500">{mailForm.kind === 'gmail'
                                ? 'the App Password from myaccount.google.com/apppasswords (2-Step Verification on) - never the Gmail password'
                                : 'the Microsoft 365 app with Mail.Read on this mailbox'} - read once before it is kept; encrypted on the server and never shown again</span>
                        </>
                    )}
                </div>
            )}
        </div></Card>
    );
}
